import React from 'react'

import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'

import SubmissionCardModalLicenseInboxCarouselEducation from '@components/Submission/Card/Modal/License/InboxCarouselEducation'
import { RequestLicense } from '@components/Submission/Card/Modal/RequestLicense/RequestLicense'
import ActionIcon from '@components/UI/ActionIcon'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { INBOX_FILTER } from '@constants'
import { IModalContextProps } from '@context/Modal'
import { IMultiframeContextProps } from '@context/MultiframeControls'
import { PopupMenuContext } from '@context/PopupMenu'
import { POPUP_TRANSITION_DURATION } from '@context/PopupMenu/Provider'
import { ISubmissionListFilterContextProps } from '@context/SubmissionListFilter'
import { EventTrackingNames, LicenseStatuses } from '@enums'
import { withModal, withMultiframeControls, withSubmissionListFilter } from '@hocs'
import { IApplicationState } from '@store'
import {
  selectFirstMediaObjectWithTransactedLicense,
  selectTransactedLicenseStatus,
} from '@store/mediaObjects'
import { selectCampaignId, selectCurrentView } from '@store/router'
import {
  selectActiveSubmissionMediaObject,
  selectIsCarousel,
  selectSubmissionMediaObjectFrameNumber,
  selectSubmissionMediaObjectIds,
} from '@store/submissions'
import Theme from '@theme'
import { isEmpty, pxToRem } from '@utils'
import ActionBarIconLicenseCarouselPopupMenu from './Carousel/PopupMenu'

interface IProps {
  submissionId: number
}

interface IInternalProps
  extends IProps,
    IModalContextProps,
    IMultiframeContextProps,
    ISubmissionListFilterContextProps {}

export const ActionBarIconLicense = ({
  submissionId,
  activeFrameNumber,
  showModal,
  isFilterLicenseRelated,
}: IInternalProps) => {
  const intl = useIntl()
  const match = useRouteMatch()
  const { showPopup, hidePopup } = React.useContext(PopupMenuContext)

  const mediaObjectId = useSelector(
    (state: IApplicationState) =>
      selectActiveSubmissionMediaObject(state, submissionId, activeFrameNumber).id,
  )

  const campaignId = selectCampaignId(match) as number

  const currentView = useSelector((state: IApplicationState) => selectCurrentView(state))
  const isCarousel = useSelector((state: IApplicationState) =>
    selectIsCarousel(state, submissionId),
  )
  const isInbox = currentView === INBOX_FILTER

  const hasAtleastOneLicensed = useSelector((state: IApplicationState) =>
    !isEmpty(
      selectFirstMediaObjectWithTransactedLicense(
        state,
        selectSubmissionMediaObjectIds(state, submissionId),
        [LicenseStatuses.Licensed],
      ),
    ),
  )

  const hasAtleastOneLicenseRequested = useSelector((state: IApplicationState) =>
    !isEmpty(
      selectFirstMediaObjectWithTransactedLicense(
        state,
        selectSubmissionMediaObjectIds(state, submissionId),
        [LicenseStatuses.Requested],
      ),
    ),
  )

  const frameNumber = useSelector((state: IApplicationState) =>
    selectSubmissionMediaObjectFrameNumber(state, submissionId, mediaObjectId),
  )

  const licenseStatus = useSelector((state: IApplicationState) =>
    selectTransactedLicenseStatus(state, mediaObjectId),
  )

  const isCarouselAndInbox = isCarousel && isInbox

  const isLicenseRequestExpired = licenseStatus === LicenseStatuses.RequestExpired
  const tooltipId =
    licenseStatus && !isLicenseRequestExpired
      ? `submission.card.action.licensecontent.${licenseStatus}`
      : 'submission.card.action.licensecontent.request'

  const isHighlighted = isCarouselAndInbox
    ? hasAtleastOneLicensed || hasAtleastOneLicenseRequested
    : licenseStatus && [LicenseStatuses.Requested, LicenseStatuses.Licensed].includes(licenseStatus)

  const isDisabled =
    licenseStatus &&
    [
      LicenseStatuses.LicenseExpired,
      LicenseStatuses.ExpiredDeprecated,
      LicenseStatuses.Declined,
    ].includes(licenseStatus)

  if (isDisabled) {
    return (
      <ActionIcon
        glyph="license-content"
        color={Theme.grey600Color}
        size={1.5}
        tooltip={intl.formatMessage({ id: tooltipId })}
        highlighted={isHighlighted}
        disabled
        elementName="license"
      />
    )
  }

  const shouldShowCarouselPopup =
    isCarousel && (!licenseStatus || licenseStatus === LicenseStatuses.RequestExpired)

  const modalProps = { submissionId, frameNumber }

  const onPopupClick = () => {
    hidePopup()
    setTimeout(() => {
      showModal(RequestLicense, modalProps)
    }, POPUP_TRANSITION_DURATION)
  }

  const onClick = (event: React.SyntheticEvent) => {
    if (isCarouselAndInbox && !isFilterLicenseRelated) {
      return showModal(SubmissionCardModalLicenseInboxCarouselEducation, modalProps)
    }

    if (shouldShowCarouselPopup) {
      const target = event.target as HTMLElement
      const { left, top, width } = target.getBoundingClientRect()

      showPopup(
        <ActionBarIconLicenseCarouselPopupMenu frameNumber={frameNumber} onClick={onPopupClick} />,
        {
          x: pxToRem(left + width),
          y: pxToRem(top),
        },
      )
      return
    }

    showModal(RequestLicense, modalProps)
  }

  return (
    <ConnectedTrackedRoute
      eventName={EventTrackingNames.ViewLicensingOptions}
      campaignId={campaignId}
      submissionId={submissionId}
      mediaObjectId={mediaObjectId}
    >
      <ActionIcon
        glyph="license-content"
        color={Theme.grey900Color}
        size={1.5}
        onClick={onClick}
        tooltip={intl.formatMessage({ id: tooltipId })}
        highlighted={isHighlighted}
        elementName="license"
      />
    </ConnectedTrackedRoute>
  )
}

export default withMultiframeControls(withSubmissionListFilter(withModal(ActionBarIconLicense)))
